import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { components, contentfulModels, contentfulUtils } from '@glowforge/ui-common';
import { machinePropType } from 'app/selectors/machines';
import strings from 'app/strings';

import './machine-access-field.less';

const { pageStringsPropType } = contentfulModels;
const { stringSubstitution } = contentfulUtils;

const {
  Button,
  Checkbox,
  Select,
  MachineBadge,
  Tooltip,
  SVG,
} = components;

const MachineAccessField = (props) => {
  const { grants, input, isSubscriber, pageStrings } = props;

  // This has to be set to be _something_. Otherwise, the redux-form select
  // SHOWs the first item in the dropdown but that value isn't passed through
  // when the form is submitted. See:
  // https://github.com/redux-form/redux-form/issues/3226
  //
  // For now setting this to 'standard_user' aka Guest becaues that was the
  // first option in the list but it's possible we want this to be 'manager'
  // because that's the current default behavior (and future behavior for users
  // who don't have subscriptions).
  const defaultRoles = ['standard_user'];

  const selectAll = () => {
    const newValue = _.cloneDeep(input.value) || [];
    _.each(props.options, (opt) => {
      if (_.isEmpty(_.find(newValue, elem => elem.serial === opt.serial))) {
        newValue.push({
          serial: opt.serial,
          machineName: opt.machineName,
          roles: defaultRoles,
          grants,
        });
      }
    });
    input.onChange(newValue);
  };

  const removeAll = () => input.onChange([]);

  const list = _.map(props.options, (machine, index) => {
    const rowValue = _.find(input.value, elem => elem.serial === machine.serial);
    const isOwner = _.includes(machine.ownerEmails, props.email);

    const onCheckboxChange = (value) => {
      let newValue = _.cloneDeep(input.value);
      if (_.isNil(newValue) || _.isEmpty(newValue)) newValue = [];
      if (value) {
        newValue.push({
          serial: machine.serial,
          machineName: machine.machineName,
          roles: defaultRoles,
          grants,
        });
      } else {
        _.remove(newValue, elem => elem.serial === machine.serial);
      }

      return input.onChange(newValue);
    };

    const onSelectChange = (value) => {
      const newValue = _.cloneDeep(input.value);
      _.find(newValue, elem => elem.serial === machine.serial).roles = [value.value];

      return input.onChange(newValue);
    };

    const youOwner = pageStrings['settings-users-owner-is-you'] || 'You';
    const nobodyOwner = pageStrings['settings-users-owner-is-nobody'] || 'Nobody';
    const ownedBy = pageStrings['settings-users-owner-is-other'] || 'Owned by: {{ownerName}}';

    let ownerName = machine.isOwner ? youOwner : machine.ownerName;
    if (_.isEmpty(ownerName)) ownerName = nobodyOwner;
    const ownerString = stringSubstitution(ownedBy, { ownerName });

    // Pull role labels from contentful if available
    const roleOptions = _.map(
      strings.USER_MACHINE_ROLES,
      (displayName, role) => ({
        value: role,
        label: pageStrings[`settings-users-role-label-${role.replace('_', '-')}`] || displayName,
        disabled: (!isSubscriber && role === 'standard_user'), // Guest role disabled for non-subscribers
      }));

    // Look up the source for react-select v1.0. Fun times.
    const optionRenderer = (option) => {
      if (!isSubscriber && option.value === 'standard_user') {
        return (
          <span className='optionContainer'>
            <span>
              {option.label}
            </span>
            <SVG
              className='clearSvg'
              id='premium-gem' />
          </span>
        );
      }
      return (
        <span className='optionContainer'>{option.label}</span>
      );
    };

    // Default role depends on if you are a subscriber or not.
    // If the user has no roles, then they are assigned a default role
    const roleValue = isSubscriber ? _.first(_.get(rowValue, 'roles')) : 'manager';
    const roleSelectDisabled = _.isNil(rowValue);
    let roleSelect;
    if (isOwner) {
      roleSelect = pageStrings['settings-users-role-label-owner'] || 'Owner';
    } else {
      roleSelect = (<Select
        name={`${machine.serial}-role`}
        value={roleSelectDisabled ? '' : roleValue}
        searchable={false}
        clearable={false}
        options={roleOptions}
        onChange={onSelectChange}
        disabled={roleSelectDisabled}
        optionRenderer={optionRenderer} />);
    }

    return (
      <tr key={index} className={isOwner ? 'disabled' : ''}>
        <td className='toggle'>
          <Checkbox
            {...input}
            id={machine.serial}
            value={_.isObject(rowValue)}
            className='machine'
            name={machine.serial}
            onChange={onCheckboxChange}
            disabled={isOwner} />
        </td>
        <td className='machineName'>
          <Tooltip position='top' infoText={ownerString}>
            <label htmlFor={machine.serial}>
              {machine.machineName} <MachineBadge model={machine.model} />
            </label>
          </Tooltip>
        </td>
        <td className='role'>
          {roleSelect}
        </td>
      </tr>
    );
  });

  let errorMessage;
  if (!_.get(props.meta, 'valid')
    && (_.get(props.meta, 'dirty') || _.get(props.meta, 'touched'))) {
    errorMessage = <span className='errorMessage'> - {_.get(props.meta, 'error')}</span>;
  }

  return (
    <div className='MachineAccessField'>
      <table className='styledTable'>
        <thead>
          <tr className='header label'>
            <th className='toggle'>
              <div>
                <Button type='text' onClick={selectAll}>All</Button>
                <span className='divider'>/</span>
                <Button type='text' onClick={removeAll}>None</Button>
              </div>
            </th>
            <th>Printer {errorMessage}</th>
            <th>Role</th>
          </tr>
        </thead>
        <tbody>{list}</tbody>
      </table>
    </div>
  );
};

MachineAccessField.propTypes = {
  email: PropTypes.string,
  grants: PropTypes.arrayOf(PropTypes.string),
  input: PropTypes.shape({ onChange: PropTypes.func }).isRequired,
  isSubscriber: PropTypes.bool,
  meta: PropTypes.shape({
    valid: PropTypes.bool.isRequired,
    touched: PropTypes.bool.isRequired,
    error: PropTypes.string,
  }).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape(machinePropType)).isRequired,
  pageStrings: pageStringsPropType.isRequired,
};

MachineAccessField.defaultProps = {
  email: '',
  grants: [],
  isSubscriber: false,
};

export default MachineAccessField;




// WEBPACK FOOTER //
// ./app/components/settings/machine-access-field.jsx